import React from 'react';
import PageSection from '../../components/PageSection';
import DataTable from '../../components/DataTable';
import { useAPI } from '../../api';
import Avatar from '../../components/Avatar';
import RawJsonDisplay from '../../components/RawJsonDisplay';
import { UserInfo } from 'types';
import { OrgContextLink, useOrgContext } from 'components/OrgContextProvider';

interface UsersListContainerProps {
	users: UserInfo[];
	onChanged: () => void;
}

const UsersListContainer = ({ users, onChanged }: UsersListContainerProps) => {
	const api = useAPI();

	const ctx = useOrgContext();

	const [userBatchFile, setUserBatchFile] = React.useState<File | null>(null);
	const [inviteUsername, setInviteUsername] = React.useState('');
	const [inviteDisplay, setInviteDisplay] = React.useState('');
	const [inviteRole, setInviteRole] = React.useState('');
	const [roles, setRoles] = React.useState<{ id: string; name: string }[]>([]);

	React.useEffect(() => {
		api.get<{ id: string; name: string }[]>(`${ctx?.apiBase}/roles`)
		.then(json => { setRoles(json); if (json.length > 0) setInviteRole(json[0].id); });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const batchCreateUsers = () => {
		if (!userBatchFile) {
			return;
		}

		// Read the file
		const reader = new FileReader();

		reader.onload = (e) => {
			const text = e.target?.result as string;
			const lines = text.split('\n').map(line => line.trim()).filter(line => line);

			const batchUsers = lines.map(line => {
				const parts = line.split(',');

				return {
					username: parts[0],
					display: parts[1],
					password: parts[2]
				};
			});

			api.post(`${ctx?.apiBase}/users`, batchUsers)
			.then(() => onChanged());
		};

		reader.readAsText(userBatchFile);
	};

	const deleteUser = (user: UserInfo) => {
		if (window.confirm(`Delete user ${user.username}?`)) {
			api.delete(`${ctx?.apiBase}/users/${user.id}`)
			.then(() => onChanged());
		}
	};

	const resetAllAvatars = () => {
		if (window.confirm('Reset all avatars?')) {
			api.post(`${ctx?.apiBase}/users/all/avatar/reset`, {})
			.then(() => {
				onChanged();
				window.location.reload();
			});
		}
	};

	const resendInvite = (user: UserInfo) => {
		api.post(`${ctx?.apiBase}/users/${user.id}/resendinvite`, {})
		.then(() => {
			alert('Invite resent');
		});
	};

	const inviteUser = () => {
		if (!inviteUsername) { alert('Username/email is required'); return; }
		if (!inviteRole) { alert('Role is required'); return; }

		api.post(`${ctx?.apiBase}/users/invite`, { username: inviteUsername, role: inviteRole, display: inviteDisplay || undefined })
		.then(() => {
			alert(`Invite sent to ${inviteUsername}`);
			setInviteUsername('');
			setInviteDisplay('');
			onChanged();
		});
	};

	return (
		<div>
			<PageSection title="Users" level={2}>
				<DataTable<UserInfo>
					data={users}
					columns={[
						{ label: '', key: 'avatar', render: (user) => <Avatar src={`/api/users/public/${user.id}/avatar?size=small`} alt="" size="24px" /> },
						{ label: 'Username', key: 'username', render: (user) => <OrgContextLink to={"/settings/users/" + user.id}>{user.username}</OrgContextLink> },
						{ label: 'Display Name', key: 'display' },
						{ label: 'ID', key: 'id' },
						{ label: 'Status', key: 'status' },
						{ label: 'Access', key: 'access', render: (user) => {
							const memberships = user.accountMemberships ?? [];
							const showAccounts = memberships.length > 1 || (user.access.length === 0 && memberships.length > 0);
							return <>
								{showAccounts && memberships.map((m, i) => (
									<span key={`acct-${i}`}>Account: {m.accountName} - {m.role}<br /></span>
								))}
								{user.access.map((a, i) => {
									let contextLabel: string;
									if (a.tenantID === 'system') {
										contextLabel = 'System';
									} else {
										const tenant = ctx?.availableTenants?.find(t => t.id === a.tenantID);
										const workspace = a.workspaceID ? tenant?.workspaces?.find(w => w.id === a.workspaceID) : null;
										if (workspace) contextLabel = `Workspace: ${tenant?.name ?? a.tenantID} / ${workspace.name}`;
										else if (tenant) contextLabel = `Tenant: ${tenant.name}`;
										else if (a.workspaceID) contextLabel = `${a.tenantID}/${a.workspaceID}`;
										else contextLabel = a.tenantID;
									}
									const roleName = roles.find(r => r.id === a.role)?.name ?? a.role;
									return <span key={`access-${i}`}>{contextLabel} - {roleName}<br /></span>;
								})}
							</>;
						} },
						{ label: 'Strategies', key: 'auth.strategies' },
						{ label: 'Last Strategy', key: 'auth.lastStrategy' },
						{ label: '2FA', key: 'auth.twoFactor.enabled', render: (user) => user.auth.twoFactor?.enabled ? 'Enabled' : 'Disabled' },
					]}
					tools={[
						{ condition: (item) => item.status === 'invited', label: 'Resend Invite', action: (user) => resendInvite(user) },
						{ label: 'Delete', action: (user) => deleteUser(user) }
					]}
					tableTools={[
						{ label: 'Create New User...', render: () => <OrgContextLink to="/settings/users/new">Create New User...</OrgContextLink> },
						{ label: 'Reset All Avatars', action: resetAllAvatars }
					]}
				/>
			</PageSection>

			<PageSection title="Invite User" level={3}>
				<table>
					<tbody>
						<tr>
							<td>Invite to</td>
							<td>{ctx?.tenantID ? (ctx.workspaceID ? `Workspace: ${ctx.workspace?.name}` : `Tenant: ${ctx.tenant?.name}`) : 'System'}</td>
						</tr>
						<tr>
							<td>Username / Email</td>
							<td><input type="text" value={inviteUsername} onChange={e => setInviteUsername(e.target.value)} /></td>
						</tr>
						<tr>
							<td>Display Name</td>
							<td><input type="text" value={inviteDisplay} onChange={e => setInviteDisplay(e.target.value)} placeholder="Optional" /></td>
						</tr>
						<tr>
							<td>Role</td>
							<td>
								<select value={inviteRole} onChange={e => setInviteRole(e.target.value)}>
									{roles.map(role => (
										<option key={role.id} value={role.id}>{role.name}</option>
									))}
								</select>
							</td>
						</tr>
						<tr>
							<td></td>
							<td><input type="button" value="Send Invite" onClick={inviteUser} /></td>
						</tr>
					</tbody>
				</table>
			</PageSection>

			<PageSection title="Batch Create Users" level={3}>
				<p>
					You can batch create users by uploading a CSV file with the following columns:
				</p>

				<ul>
					<li>username/email</li>
					<li>display</li>
					<li>password (optional - will enable 'local' auth)</li>
				</ul>

				<input type="file" name="file" onChange={e => setUserBatchFile(e.target.files?.[0] ?? null)} />
				<button onClick={batchCreateUsers}>Upload and Create Users</button>
			</PageSection>

			<PageSection title="Raw Data" level={3}>
				<RawJsonDisplay data={users} />
			</PageSection>
		</div>
	);
}

export default UsersListContainer;
